import { Product, Category, Subcategory, Message, Order, CategoryWithSubcategories } from '../types';
import { toast } from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

interface ApiResponse<T> {
  data: T;
  error?: string;
  status: number;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    
    // Handle specific error cases
    if (response.status === 500) {
      if (error.message?.includes('Access denied for user')) {
        throw new ApiError(
          response.status,
          'The server is currently experiencing technical difficulties. Please try again later.'
        );
      }
      throw new ApiError(
        response.status,
        'Server error occurred. Our team has been notified.'
      );
    }
    
    throw new ApiError(response.status, error.message || 'An error occurred');
  }

  const data = await response.json();
  return {
    data,
    status: response.status
  };
};

const getHeaders = (isAdmin = false, isFormData = false) => {
  const headers: Record<string, string> = {
    'Accept': 'application/json'
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Always require API key for all requests
  if (!API_KEY) {
    throw new ApiError(401, 'API key is missing');
  }
  headers['X-API-Key'] = API_KEY;

  // For admin routes, also require JWT token
  if (isAdmin) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new ApiError(401, 'Unauthorized: No token provided');
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithRetry = async <T>(
  url: string,
  options: RequestInit,
  maxRetries = 3,
  retryDelay = 1000
): Promise<Response> => {
  let lastError: Error | null = null;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || response.status !== 500) {
        return response;
      }
      lastError = new Error(`Server error: ${response.status}`);
    } catch (error) {
      lastError = error as Error;
    }
    
    if (i < maxRetries - 1) {
      await delay(retryDelay * Math.pow(2, i)); // Exponential backoff
    }
  }
  
  throw lastError;
};

const handleApiError = (error: unknown, isAdmin: boolean): never => {
  if (error instanceof ApiError) {
    if (isAdmin && (error.status === 401 || error.status === 403)) {
      localStorage.removeItem('token');
      toast.error('Session expired. Please log in again.');
      window.location.href = '/admin/login';
    }
    throw error;
  }
  throw new ApiError(500, 'Network error occurred. Please check your connection and try again.');
};

export const api = {
  async get<T>(endpoint: string, isAdmin = false): Promise<ApiResponse<T>> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'GET',
          headers: getHeaders(isAdmin)
        }
      );
      
      return handleResponse<T>(response);
    } catch (error) {
      throw handleApiError(error, isAdmin);
    }
  },

  async post<T>(endpoint: string, data: any, isAdmin = false): Promise<ApiResponse<T>> {
    try {
      const isFormData = data instanceof FormData;
      const response = await fetchWithRetry(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'POST',
          headers: getHeaders(isAdmin, isFormData),
          body: isFormData ? data : JSON.stringify(data)
        }
      );

      return handleResponse<T>(response);
    } catch (error) {
      throw handleApiError(error, isAdmin);
    }
  },

  async put<T>(endpoint: string, data: any, isAdmin = false): Promise<ApiResponse<T>> {
    try {
      const isFormData = data instanceof FormData;
      const response = await fetchWithRetry(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'PUT',
          headers: getHeaders(isAdmin, isFormData),
          body: isFormData ? data : JSON.stringify(data)
        }
      );

      return handleResponse<T>(response);
    } catch (error) {
      throw handleApiError(error, isAdmin);
    }
  },

  async delete<T>(endpoint: string, isAdmin = false): Promise<ApiResponse<T>> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'DELETE',
          headers: getHeaders(isAdmin)
        }
      );

      return handleResponse<T>(response);
    } catch (error) {
      throw handleApiError(error, isAdmin);
    }
  },

  async patch<T>(endpoint: string, data: any, isAdmin = false): Promise<ApiResponse<T>> {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'PATCH',
          headers: getHeaders(isAdmin),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<T>(response);
    } catch (error) {
      throw handleApiError(error, isAdmin);
    }
  }
};

// API endpoints
export const productsApi = {
  getAll: () => api.get<Product[]>('/products'),
  getById: (id: string) => api.get<Product>(`/products/${id}`),
  getBySlug: (slug: string) => api.get<Product>(`/products/slug/${slug}`),
  getByCategory: (categoryId: string) => api.get<Product[]>(`/products?category=${categoryId}`)
};

export const categoriesApi = {
  getAll: () => api.get<Category[]>('/categories'),
  getById: (id: string) => api.get<Category>(`/categories/${id}`),
  getSubcategories: (categoryId: number) => api.get<Subcategory[]>(`/categories/${categoryId}/subcategories`)
};

export const ordersApi = {
  create: (data: any) => api.post<Order>('/orders', data),
  getAll: () => api.get<Order[]>('/orders'),
  getById: (id: string) => api.get<Order>(`/orders/${id}`)
};

export const messagesApi = {
  create: (data: any) => api.post<Message>('/messages', data),
  getAll: () => api.get<Message[]>('/messages')
};

export const countsApi = {
  get: () => api.get<any>('/counts', true)
};

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface OrdersResponse {
  orders: Order[];
}

interface MessagesResponse {
  messages: Message[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const adminApi = {
  products: {
    getAll: () => api.get<Product[]>('/admin/products', true),
    getById: (id: string) => api.get<Product>(`/admin/products/${id}`, true),
    create: (data: any) => api.post<Product>('/admin/products', data, true),
    update: (id: string, data: any) => api.put<Product>(`/admin/products/${id}`, data, true),
    delete: (id: string) => api.delete<any>(`/admin/products/${id}`, true),
    toggleStatus: (id: string, active: boolean) => api.put<Product>(`/admin/products/${id}/status`, { active }, true)
  },
  categories: {
    getAll: () => api.get<CategoryWithSubcategories[]>('/categories?include=subcategories', true),
    create: (data: any) => api.post<Category>('/categories', data, true),
    update: (id: string, data: any) => api.put<Category>(`/categories/${id}`, data, true),
    delete: (id: string) => api.delete<any>(`/categories/${id}`, true),
    getSubcategories: (categoryId: number) => api.get<Subcategory[]>(`/categories/${categoryId}/subcategories`, true),
    createSubcategory: (categoryId: number, data: any) => api.post<Subcategory>(`/categories/${categoryId}/subcategories`, data, true),
    updateSubcategory: (categoryId: number, subcategoryId: number, data: any) => api.put<Subcategory>(`/categories/${categoryId}/subcategories/${subcategoryId}`, data, true),
    deleteSubcategory: (categoryId: number, subcategoryId: number) => api.delete<any>(`/categories/${categoryId}/subcategories/${subcategoryId}`, true)
  },
  orders: {
    getAll: async () => {
      const response = await api.get<Order[]>('/orders/admin/orders', true);
      // Transform the response to match the Order interface
      const transformedOrders = response.data.map((order: any) => ({
        id: String(order.id),
        order_number: order.order_number,
        user_id: null,
        status: order.status,
        total_amount: order.total,
        shipping_fee: "0.00",
        payment_method: order.paymentMethod,
        payment_status: order.status,
        notes: "",
        customer_name: order.customer,
        customer_email: order.customer_email,
        customer_phone: "",
        shipping_address: order.shipping_address,
        shipping_status: order.status,
        tracking_number: "",
        tracking_url: "",
        estimated_delivery_date: null,
        actual_delivery_date: null,
        shipping_provider: "",
        delivery_instructions: "",
        created_at: order.date,
        updated_at: order.date,
        itemsDetails: order.itemsDetails || []
      }));
      return {
        ...response,
        data: transformedOrders
      };
    },
    getById: async (orderId: string) => {
      const response = await api.get<any>(`/orders/admin/orders/${orderId}`, true);
      const o = response.data;
      const transformed: Order = {
        id: String(o.id),
        order_number: o.id,
        user_id: null,
        status: o.status,
        total_amount: String(o.total),
        shipping_fee: '0.00',
        payment_method: o.paymentMethod,
        payment_status: o.status,
        notes: '',
        customer_name: o.customer,
        customer_email: o.customer_email,
        customer_phone: '',
        shipping_address: o.shipping_address,
        shipping_status: o.status,
        tracking_number: '',
        tracking_url: '',
        estimated_delivery_date: null,
        actual_delivery_date: null,
        shipping_provider: '',
        delivery_instructions: '',
        created_at: o.date,
        updated_at: o.date,
        itemsDetails: o.itemsDetails || []
      };
      return { ...response, data: transformed };
    },
    updateStatus: (orderId: string, status: string) => api.put<Order>(`/orders/admin/order-status`, { orderId, status }, true),
    updatePaymentStatus: (orderId: string, status: string) => api.put<Order>(`/orders/admin/payment-status`, { orderId, status }, true),
    updateShippingStatus: (orderId: string, status: string) => api.put<Order>(`/orders/admin/shipping-status`, { orderId, status }, true)
  },
  messages: {
    getAll: async () => {
      const response = await api.get<MessagesResponse>('/messages', true);
      return {
        ...response,
        data: response.data.messages
      };
    },
    getById: (id: string) => api.get<Message>(`/messages/${id}`, true),
    markAsRead: (id: string) => api.patch<Message>(`/messages/${id}/read`, {}, true),
    updateStatus: (id: string, status: string) => api.patch<Message>(`/messages/${id}/status`, { status }, true)
  },
  dashboard: {
    getStats: () => Promise.resolve({
      data: {
        unreadMessagesCount: 5,
        pendingOrdersCount: 3,
        totalSales: 150000,
        totalOrders: 25,
        lowStockProducts: 8,
        recentOrders: [],
        chartData: [
          { name: 'Jan', sales: 4000 },
          { name: 'Feb', sales: 3000 },
          { name: 'Mar', sales: 5000 },
          { name: 'Apr', sales: 2780 },
          { name: 'May', sales: 1890 },
          { name: 'Jun', sales: 2390 }
        ],
        recentActivities: [
          { message: 'New order #1234 received', time: '2 minutes ago' },
          { message: 'Payment received for order #1233', time: '5 minutes ago' },
          { message: 'Order #1232 marked as shipped', time: '10 minutes ago' }
        ]
      },
      status: 200
    }),
    getRecentOrders: async () => {
      const response = await api.get<Order[]>('/orders/admin/orders', true);
      // Transform the response to match the Order interface
      const transformedOrders = response.data.map((order: any) => ({
        id: String(order.id),
        order_number: order.order_number,
        user_id: null,
        status: order.status,
        total_amount: order.total,
        shipping_fee: "0.00",
        payment_method: order.paymentMethod,
        payment_status: order.status,
        notes: "",
        customer_name: order.customer,
        customer_email: order.customer_email,
        customer_phone: "",
        shipping_address: order.shipping_address,
        shipping_status: order.status,
        tracking_number: "",
        tracking_url: "",
        estimated_delivery_date: null,
        actual_delivery_date: null,
        shipping_provider: "",
        delivery_instructions: "",
        created_at: order.date,
        updated_at: order.date,
        itemsDetails: order.itemsDetails || []
      }));
      return {
        ...response,
        data: transformedOrders
      };
    },
    getLowStockProducts: () => api.get<Product[]>('/admin/products', true)
  }
}; 